import React, { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { tr } from '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { AppButton, AppCheckbox, SignaturePad } from '../atoms';
import AppUnderlineInput from '../atoms/AppUnderlineInput/AppUnderlineInput';
import formValidator from '../../utility/formValidator';
import {
  AGREEMENT_FORM_INIT,
  AGREEMENT_TEMPLATE_MOCK,
  SUBSCRIPTION_AGREEMENT_CONTENT,
  getSubscriptionAgreementContent,
} from '../../constant/agreement.constant';
import { IAgreementFormData, IAgreementTemplate } from '../../interface/agreement';
import { IAgreementPreFillData } from '../../interface/auth.interface';
import { PrimaryModal, ModalHandle } from '../Modals';

interface SubscriptionAgreementModalProps {
  onSubmit: (data: IAgreementFormData) => void;
  isLoading?: boolean;
  preFillData?: IAgreementPreFillData;
  onClose?: () => void;
}

const SubscriptionAgreementModal = forwardRef<ModalHandle, SubscriptionAgreementModalProps>(
  ({ onSubmit, isLoading = false, preFillData, onClose }, ref) => {
  const { t, i18n } = useTranslation();
  const subContent = getSubscriptionAgreementContent(i18n.language);
  const COMPANY_SIGNATURE = {
    name: 'Sanlabz (Private) Limited',
    representativeName: tr('g.authorized_signatory', 'Authorized Signatory'),
    representativeTitle: 'Director',
    date: new Date().toLocaleDateString(),
  };
  const contentRef = useRef<HTMLDivElement>(null);

  const initialFormData = useMemo<IAgreementFormData>(() => {
    if (!preFillData) return AGREEMENT_FORM_INIT;

    const organization = preFillData.organizations?.[0];
    const userFullName = `${preFillData.user?.first_name || ''} ${
      preFillData.user?.last_name || ''
    }`.trim();
    const address = organization?.physical_address;
    const organizationAddress = address
      ? [address.address_line_1, address.address_line_2, address.city, address.country]
          .filter(Boolean)
          .join(', ')
      : '';

    return {
      ...AGREEMENT_FORM_INIT,
      organizationName: {
        ...AGREEMENT_FORM_INIT.organizationName,
        value: organization?.name || '',
      },
      organizationAddress: {
        ...AGREEMENT_FORM_INIT.organizationAddress,
        value: organizationAddress,
      },
      businessRegistrationNumber: {
        ...AGREEMENT_FORM_INIT.businessRegistrationNumber,
        value: organization?.business_registration_no || organization?.registration_no || '',
      },
      primaryContactName: {
        ...AGREEMENT_FORM_INIT.primaryContactName,
        value: userFullName,
      },
      primaryContactTitle: {
        ...AGREEMENT_FORM_INIT.primaryContactTitle,
        value: preFillData.user?.title || '',
      },
    };
  }, [preFillData]);

  const [formData, setFormData] = useState<IAgreementFormData>(initialFormData);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [templateData] = useState<IAgreementTemplate>(AGREEMENT_TEMPLATE_MOCK);

  useEffect(() => {
    if (preFillData) {
      setFormData(initialFormData);
    }
  }, [preFillData, initialFormData]);

  const modalRef = useRef<ModalHandle>(null);

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setFormData(initialFormData);
      setIsScrolledToBottom(false);
      modalRef.current?.openModal();
    },
    closeModal: () => {
      modalRef.current?.closeModal();
    },
  }));

  const onChangeInput = (field: keyof IAgreementFormData, value: any) => {
    setFormData((prev: IAgreementFormData) => ({
      ...prev,
      [field]: { ...prev[field], value, error: null },
    }));
  };

  const handleScrollAgreement = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    setIsScrolledToBottom(isAtBottom);
  };

  const handleSave = async () => {
    if (!isScrolledToBottom) return;

    if (
      !formData.primaryContactSignature.value ||
      formData.primaryContactSignature.value.trim() === ''
    ) {
      setFormData((prev: IAgreementFormData) => ({
        ...prev,
        primaryContactSignature: {
          ...prev.primaryContactSignature,
          error:
            tr('fe_modals.signature_is_required_please_draw_your_signature_on_the', tr('g.x_signature_is_required_please_draw_your_signature_on_', 'Signature is required. Please draw your signature on the canvas before proceeding.')),
        },
      }));
      return;
    }

    const [validateData, isValid] = await formValidator(formData);

    if (!isValid) {
      setFormData((prev: IAgreementFormData) => ({
        ...prev,
        ...validateData,
      }));
      return;
    }

    onSubmit(formData);
  };

  const handleCancel = () => {
    setFormData(AGREEMENT_FORM_INIT);
    setIsScrolledToBottom(false);
    modalRef.current?.closeModal();
    onClose?.();
  };

  const modalContent = (
    <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 250px)' }}>
      {/* Scrollable Document */}
      <div
        className="flex-1 overflow-y-auto px-2 py-4"
        ref={contentRef}
        onScroll={handleScrollAgreement}
      >
        <div className="text-sm leading-relaxed text-gray-800 space-y-4">
              {/* Opening Paragraph */}
              <p>
                This Master Subscription Agreement (&quot;Agreement&quot;) is entered into as of
                this
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.startDay.value}
                    onChange={(e) => onChangeInput('startDay', e.target.value)}
                    width="w-12"
                    inline
                    placeholder="__"
                  />
                  ]
                </span>{' '}
                day of{' '}
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.startMonth.value}
                    onChange={(e) => onChangeInput('startMonth', e.target.value)}
                    width="w-28"
                    inline
                    placeholder="___________"
                  />
                  ]
                </span>
                ,{' '}
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.startYear.value}
                    onChange={(e) => onChangeInput('startYear', e.target.value)}
                    width="w-16"
                    inline
                    placeholder="____"
                  />
                  ]
                </span>{' '}
                (&quot;Effective Date&quot;), by and between:
              </p>

              {/* Parties */}
              <p>
                <strong>{templateData.companyName}</strong>, Reg No. {templateData.companyRegNo}{' '}
                (&quot;Company&quot;); and{' '}
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.organizationName.value}
                    onChange={(e) => onChangeInput('organizationName', e.target.value)}
                    width="w-64"
                    inline
                    placeholder="___________________________"
                  />
                  ]
                </span>
                , a registered educational <strong>{t('common.school_institute_centre', 'School, Institute, or Centre')}</strong> located at{' '}
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.organizationAddress.value}
                    onChange={(e) => onChangeInput('organizationAddress', e.target.value)}
                    width="w-64"
                    inline
                    placeholder="___________________________"
                  />
                  ]
                </span>
                , with Business Registration #{' '}
                <span className="inline-flex items-center">
                  [
                  <AppUnderlineInput
                    value={formData.businessRegistrationNumber.value}
                    onChange={(e) => onChangeInput('businessRegistrationNumber', e.target.value)}
                    width="w-48"
                    inline
                    placeholder="__________________"
                  />
                  ]
                </span>{' '}
                (&quot;Customer&quot;).
              </p>

              {/* Agreement Content Sections */}
              <p className="whitespace-pre-wrap mt-4">{subContent.section1}</p>
              <p className="whitespace-pre-wrap mt-4">{subContent.section2}</p>
              <p className="whitespace-pre-wrap mt-4">{subContent.section3}</p>

              {/* Signature Section */}
              <div className="mt-8 pt-6 border-t-2 border-gray-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Company Signature */}
                  <div>
                    <p className="font-bold mb-4">{t('subscription.for_sanlabz', 'For Sanlabz (Private) Limited')}</p>
                    <div className="space-y-3 text-sm">
                      <p>
                        Signature:{' '}
                        <span className="inline-block w-40 border-b border-gray-400">
                          {COMPANY_SIGNATURE.representativeName}
                        </span>
                      </p>
                      <p>
                        Name:{' '}
                        <span className="inline-block w-48 border-b border-gray-400">
                          {COMPANY_SIGNATURE.name}
                        </span>
                      </p>
                      <p>
                        Title:{' '}
                        <span className="inline-block w-48 border-b border-gray-400">
                          {COMPANY_SIGNATURE.representativeTitle}
                        </span>
                      </p>
                      <p>
                        Date:{' '}
                        <span className="inline-block w-48 border-b border-gray-400">
                          {COMPANY_SIGNATURE.date}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Customer Signature */}
                  <div>
                    <p className="font-bold mb-4">
                      For{' '}
                      <AppUnderlineInput
                        value={formData.organizationName.value}
                        onChange={(e) => onChangeInput('organizationName', e.target.value)}
                        width="w-56"
                        inline
                        placeholder="_______________________________"
                      />
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-[max-content_1fr] gap-x-4 items-center">
                        <div className="text-sm text-gray-700">{t('common.signature_colon', 'Signature:')}</div>
                        <div>
                          <span className="inline-block w-40 border-b border-gray-400">
                            {formData.primaryContactSignature.value && (
                              <img
                                src={formData.primaryContactSignature.value}
                                alt={tr('fe_modals.signature', "Signature")}
                                className="max-w-full h-auto"
                              />
                            )}
                          </span>
                        </div>

                        <div className="text-sm text-gray-700">{t('common.name_colon', 'Name:')}</div>
                        <div>
                          <span className="inline-flex items-center">
                            [
                            <AppUnderlineInput
                              value={formData.primaryContactName.value}
                              onChange={(e) => onChangeInput('primaryContactName', e.target.value)}
                              width="w-48"
                              inline
                              placeholder="____________________________"
                            />
                            ]
                          </span>
                        </div>

                        <div className="text-sm text-gray-700">{t('common.title_colon', 'Title:')}</div>
                        <div>
                          <span className="inline-flex items-center">
                            [
                            <AppUnderlineInput
                              value={formData.primaryContactTitle.value}
                              onChange={(e) => onChangeInput('primaryContactTitle', e.target.value)}
                              width="w-48"
                              inline
                              placeholder="_____________________________"
                            />
                            ]
                          </span>
                        </div>

                        <div className="text-sm text-gray-700">{t('common.date_colon', 'Date:')}</div>
                        <div>
                          <span className="inline-block w-48 border-b border-gray-400 text-gray-600">
                            {formData.agreementDate.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-4"></div>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="px-2 py-5 bg-gray-50 border-t-2 border-gray-300 space-y-4">
            {!isScrolledToBottom && (
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-xs text-yellow-800 font-poppins font-semibold">{t('g.please_scroll_and_read_the_complete_agreement_document_', 'Please scroll and read the complete agreement document above before proceeding.')}</p>
              </div>
            )}

            <div className="mb-6">
              <SignaturePad
                label={t('subscription.form_signature', 'Customer/School Representative Signature *')}
                value={formData.primaryContactSignature.value}
                onChange={(sig) => onChangeInput('primaryContactSignature', sig)}
                onError={(err) => {
                  if (err) {
                    setFormData((prev) => ({
                      ...prev,
                      primaryContactSignature: {
                        ...prev.primaryContactSignature,
                        error: err,
                      },
                    }));
                  }
                }}
                disabled={!isScrolledToBottom || isLoading}
                error={formData.primaryContactSignature.error}
                showErrorMessage={true}
                showClearButton={true}
                showStatus={true}
                height="h-40"
                width="w-full"
                penColor="#6c4e9a"
                ariaLabel={t('common.aria_draw_signature', 'Draw your signature on the canvas')}
              />
            </div>

            <div className="pt-2">
              <div className="flex items-start">
                <AppCheckbox
                  label={subContent.acknowledgmentText}
                  checked={formData.acknowledgmentAccepted.value}
                  onChange={(e) => onChangeInput('acknowledgmentAccepted', e.target.checked)}
                  disabled={!isScrolledToBottom || isLoading}
                />
              </div>
              {formData.acknowledgmentAccepted.error && (
                <p className="text-xs text-red-600 mt-1">{formData.acknowledgmentAccepted.error}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-3">
              <AppButton
                label={t('common.button_cancel')}
                outlined
                onClick={handleCancel}
                disabled={isLoading}
                className="px-6"
              />
              <AppButton
                label={t('subscription.form_accept_agreement', 'Accept Agreement')}
                onClick={handleSave}
                disabled={
                  !isScrolledToBottom ||
                  !formData.acknowledgmentAccepted.value ||
                  !formData.primaryContactSignature.value ||
                  isLoading
                }
                loading={isLoading}
                className="px-6"
              />
            </div>
          </div>
        </div>
  );

  return (
    <PrimaryModal
      ref={modalRef}
      title={subContent.title}
      showHeader
      onClose={handleCancel}
      width="1000px"
      content={modalContent}
    />
  );
  },
);

SubscriptionAgreementModal.displayName = 'SubscriptionAgreementModal';

export default SubscriptionAgreementModal;
